import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { ERROR_TOAST_CONFIG, isEmptyObject, isValidImageExtension } from "../../helpers/functions";
import { Storage } from "aws-amplify";
import { toast } from 'react-toastify';
import EventCodeInput from "./eventCodeInput";
import EventsService from "../../services/eventsService";
import EventSellChecklist from "./eventSellChecklist";
import PosterPreview from "./posterPreview";
import { isEventPublished } from "../../helpers/eventPublication";
import Modal from "../generic/modal";
import { RevAlert, RevBarra, RevButton, RevCard, RevDanger, RevField, RevSwitch, RevTipRow } from "../ds";

/**
 * Forma del esqueleto de la pantalla de alta (`/events/new`), en el módulo dueño de ella:
 * la card de identidad con sus dos rejillas de dos campos (la fila de fecha fin solo
 * existe con la duración en varios días, que el formulario recién abierto no tiene), el
 * aviso de créditos y la card de pasos del aside. `.rev-cols` va sin `--flush` porque el
 * alta lleva título de página encima.
 */
export const esqueletoAlta = (t) => ({
    bloques: [
        {
            card: {
                campos: [
                    [t('event.basicData.nameInput.help'), null],
                    [t('event.new.code.help'), t('event.basicData.multiDate.help')],
                ],
            },
        },
        { alerta: { titulo: t('event.new.credits.title'), texto: t('event.new.credits.body') } },
    ],
    aside: [{
        card: {
            tips: [
                { n: '01', titulo: t('event.new.steps.s1.title'), detalle: t('event.new.steps.s1.detail') },
                { n: '02', titulo: t('event.new.steps.s2.title'), detalle: t('event.new.steps.s2.detail') },
                { n: '03', titulo: t('event.new.steps.s3.title'), detalle: t('event.new.steps.s3.detail') },
            ],
        },
    }],
    barra: [t('generic.cancelButton.text'), t('event.basicData.createButton.text')],
    clases: { cols: 'rev-cols' },
});

const EventForm = ({ event, dispatchCreateOrUpdate, dispatchCancel, dispatchDelete, loading }) => {
    const { t } = useTranslation();
    const imageInputRef = useRef(null);
    const [imageSrc, setImageSrc] = useState(undefined);
    const [enableSubmit, setEnableSubmit] = useState(false);
    const [selectedImage, setSelectedImage] = useState(undefined);
    const [isLoadingImage, setIsLoadingImage] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [saved, setSaved] = useState(false);
    const isEdit = !!event?.id;

    const date = new Date();
    const today = moment(date).format('YYYY-MM-DD');

    const { register, watch, setValue, setError, clearErrors, handleSubmit, reset, formState: { errors, isDirty, isSubmitting } } = useForm({
        defaultValues: {
            "code": "",
            "name": "",
            "dateFrom": today,
            "dateTo": today,
            "eventType": t('event.basicData.eventType.race'),
            "active": false,
            "multiDate": false,
            "hasCustomPosterImage": false
        }
    });
    const multiDate = watch("multiDate");
    const dateFromValue = watch("dateFrom");

    // Guardar con cartel tarda segundos (subida + copia al cartel público): recargar a mitad
    // deja el evento sin el cambio, aunque la vista previa ya enseñe el cartel nuevo.
    useEffect(() => {
        if (!isSubmitting) return undefined;
        const warn = e => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [isSubmitting]);

    useEffect(() => { if (isDirty || enableSubmit) setSaved(false); }, [isDirty, enableSubmit]);

    useEffect(() => {
        if (!isEmptyObject(event)) {
            const dateFrom = moment(event.dateFrom).format('YYYY-MM-DD');
            const dateTo = moment(event.dateTo).format('YYYY-MM-DD');
            let multiDate = !moment(dateFrom).isSame(dateTo, 'day');
            reset({
                "code": event.code,
                "name": event.name,
                "dateFrom": dateFrom,
                "dateTo": dateTo,
                "eventType": event.eventType,
                "active": event.active,
                "multiDate": multiDate,
                "hasCustomPosterImage": event.hasCustomPosterImage
            });
            if (event.hasCustomPosterImage === true) {
                const posterImagePath = `${event.id}/Configuration/${event.posterImageName}`;
                setIsLoadingImage(true);
                setTimeout(() => {
                    Storage.get(posterImagePath)
                        .then(signedUrl => {
                            setImageSrc(signedUrl);
                            setIsLoadingImage(false);
                        })
                        .catch(error => {
                            console.log(error);
                            setIsLoadingImage(false);
                        });
                }, 700);
            } else {
                setImageSrc(undefined);
            }
        }
    }, [event, reset]);

    const onSubmit = async (data) => {
        clearErrors('root');
        setSaved(false);
        const dateFrom = moment(data.dateFrom + "T10:00:00");
        const dateTo = !multiDate ? moment(data.dateFrom + "T10:01:00") : moment(data.dateTo + "T10:01:00");
        try {
            await dispatchCreateOrUpdate(!isEdit, data.name, data.code || undefined, dateFrom.toISOString(), dateTo.toISOString(), data.eventType, selectedImage, data.active, data.hasCustomPosterImage);
            setSelectedImage(undefined);
            setEnableSubmit(false);
            if (isEdit) setSaved(true);
        } catch (error) {
            if (error.response?.status === 409) {
                setError('code', { message: t('The code {{code}} is not available', { code: data.code }) });
            } else if (error.response?.data?.errorCode === 'INVALID_EVENT_CODE') {
                setError('code', { message: t('event.new.code.invalid') });
            } else {
                setError('root', { message: t('event.new.error.create') });
            }
        }
    };

    const validateNewCode = async (code) => {
        if (!code) return true;
        if (!/^[A-Z0-9]{8}$/.test(code)) return t('event.new.code.invalid');
        try {
            const response = await EventsService.checkCodeAvailability(code);
            return response.data === true || t('The code {{code}} is not available', { code });
        } catch {
            return t('event.new.code.checkError');
        }
    };

    const onError = (err, e) => console.log(err, e);

    const onCopyCode = () => {
        navigator.clipboard.writeText(event.code);
        toast.success(t('nav.menu.copyText'), { ...ERROR_TOAST_CONFIG, autoClose: 3000 });
    };

    const clearImageInput = () => {
        setValue('hasCustomPosterImage', false, { shouldDirty: true });
        setSelectedImage(undefined);
        imageInputRef.current.value = "";
        setImageSrc(undefined);
        setEnableSubmit(true);
    };

    const onImageSelected = (e) => {
        const selectedImage = e.target.files[0];

        if (isValidImageExtension(selectedImage.name)) {
            setEnableSubmit(true);
            setSelectedImage(selectedImage);
            if (FileReader) {
                let fileReader = new FileReader();
                fileReader.onload = function () {
                    setImageSrc(fileReader.result);
                    setValue('hasCustomPosterImage', true);
                };
                fileReader.readAsDataURL(selectedImage);
            }
        } else {
            clearImageInput();
            setSelectedImage(undefined);
            setEnableSubmit(false);
            alert("Formato no compatible");
        }
    };

    const nameField = (
        <RevField label={t('event.basicData.nameInput.label')} htmlFor="name"
            help={t('event.basicData.nameInput.help')} error={errors.name?.message}>
            <input data-testid="name" id="name" className={`rev-input${errors.name ? ' rev-input--error' : ''}`} type="text"
                placeholder={t('event.basicData.nameInput.placeholder')}
                {...register('name', { required: t('event.basicData.validationRequired') })} />
        </RevField>
    );

    const startDateField = (
        <RevField label={t('event.basicData.startDateInput.label')} htmlFor="dateFrom" error={errors.dateFrom?.message}>
            <input data-testid="dateFrom" id="dateFrom" className={`rev-input rev-num${errors.dateFrom ? ' rev-input--error' : ''}`} type="date"
                placeholder={t('event.basicData.datePlaceholder')}
                {...register("dateFrom", {
                    required: t('event.basicData.validationRequired'),
                    validate: date => moment(date).isValid()
                })} />
        </RevField>
    );

    const endDateField = multiDate && (
        <RevField label={t('event.basicData.endDateInput.label')} htmlFor="dateTo" error={errors.dateTo?.message}>
            <input data-testid="dateTo" id="dateTo" className={`rev-input rev-num${errors.dateTo ? ' rev-input--error' : ''}`} type="date"
                placeholder={t('event.basicData.datePlaceholder')}
                {...register("dateTo", {
                    required: t('event.basicData.validationRequired'),
                    validate: date => {
                        const isValidDate = moment(date).isValid();
                        const isAfterOrSame = moment(date).isAfter(moment(dateFromValue));
                        return (isValidDate && isAfterOrSame) || t('Date must be after the start date');
                    }
                })} />
        </RevField>
    );

    const durationField = (
        <RevField label={t('event.basicData.duration.label')} help={t('event.basicData.multiDate.help')}>
            <label htmlFor="multiDate" className="rev-switch-field rev-switch-field--control">
                <input
                    data-testid="multiDate"
                    type="checkbox"
                    id="multiDate"
                    className="rev-switch-input"
                    {...register("multiDate")} />
                <RevSwitch on={multiDate} />
                <span>{t('event.basicData.multiDate.label')}</span>
            </label>
        </RevField>
    );

    // Con «Varios días» activo, la fecha de fin se pone al lado de la de inicio, en la misma
    // línea: la fila no crece en alto.
    const dateFields = (
        <div className="rev-basic-dates">
            {startDateField}
            {endDateField}
        </div>
    );

    // Publicado el evento el codigo queda bloqueado (eventCodeInput), asi que la ayuda
    // deja de avisar de lo que se puede hacer y dice por que ya no se puede.
    const codeLocked = isEventPublished(event);

    const codeField = (
        <RevField label={t('event.basicData.code.label')} htmlFor="eventCode"
            help={t(codeLocked ? 'event.basicData.code.helpLocked' : 'event.basicData.code.help')}
            helpTone={codeLocked ? undefined : "warn"}>
            <EventCodeInput register={register} onCopyCode={onCopyCode} initialEventCode={event?.code} />
        </RevField>
    );

    const newCodeField = (
        <RevField label={t('event.basicData.code.label')} htmlFor="newEventCode"
            help={<span id="newEventCodeHelp">{t('event.new.code.help')}</span>} error={errors.code?.message}>
            <input id="newEventCode" className={`rev-input rev-mono${errors.code ? ' rev-input--error' : ''}`}
                type="text" maxLength={8} autoComplete="off" autoCapitalize="characters" spellCheck={false}
                placeholder={t('event.new.code.placeholder')} disabled={loading || isSubmitting}
                aria-describedby="newEventCodeHelp" aria-invalid={!!errors.code}
                {...register('code', {
                    setValueAs: value => value?.trim().toUpperCase() || '',
                    onChange: e => { e.target.value = e.target.value.toUpperCase(); },
                    validate: isEdit ? undefined : validateNewCode,
                })} />
        </RevField>
    );

    const cancelButton = (
        <RevButton data-testid="cancel" variant="secondary" onClick={e => { e.preventDefault(); dispatchCancel(); }}>
            {t('generic.cancelButton.text')}
        </RevButton>
    );

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
                {isEdit ? (
                    <div className="rev-cols rev-cols--flush">
                        <div className="rev-cols__main">
                            <RevCard title={t('event.basicData.card.identity.title')}>
                                <div className="rev-form-grid">
                                    {nameField}
                                    {dateFields}
                                </div>
                                <div className="rev-form-grid">
                                    {codeField}
                                    {durationField}
                                </div>
                                {/* Con cartel, la cruz basta para cambiarlo: el pie, el botón y los
                                    formatos solo hacen falta mientras no hay imagen. */}
                                <RevField label={t('event.basicData.headerImage.label')} help={imageSrc ? undefined : t('event.basicData.headerImage.formats')}>
                                    {/* El marco reproduce el de la galería pública: lo que el fotógrafo ve
                                        aquí es lo que verá el asistente en gallery.revopic.com. */}
                                    <PosterPreview
                                        src={imageSrc}
                                        loading={isLoadingImage}
                                        alt={t('event.basicData.headerImage.alt')}
                                        emptyText={t('event.basicData.headerImage.empty')}
                                        caption={imageSrc ? undefined : t('event.basicData.headerImage.preview')}
                                        onRemove={clearImageInput}
                                        removeLabel={t('event.basicData.headerImage.remove')} />
                                    {!imageSrc && <RevButton as="label" htmlFor="file-upload" variant="secondary" fit>
                                        {t('generic.selectFileButton.text')}
                                    </RevButton>}
                                    <input id="file-upload" type="file" className="rev-hidden" ref={imageInputRef}
                                        onChange={onImageSelected} />
                                </RevField>
                            </RevCard>

                            {typeof dispatchDelete === 'function' &&
                                <RevDanger
                                    title={t('event.deleteEvent.cardTitle')}
                                    text={t('event.deleteEvent.cardBody')}
                                    action={
                                        <RevButton variant="danger" onClick={e => { e.preventDefault(); setShowDeleteModal(true); }}>
                                            {t('event.sideNavigation.delete')}
                                        </RevButton>
                                    }
                                />
                            }
                        </div>

                        <div className="rev-basic-aside">
                            <EventSellChecklist event={event} />
                        </div>
                    </div>
                ) : (
                    <>
                        <h1 className="rev-page-title">{t('event.new.title')}</h1>
                        <div className="rev-cols">
                            <div className="rev-cols__main">
                                <RevCard title={t('event.basicData.card.identity.title')}>
                                    <div className="rev-form-grid">
                                        {nameField}
                                        {dateFields}
                                    </div>
                                    <div className="rev-form-grid">
                                        {newCodeField}
                                        {durationField}
                                    </div>
                                </RevCard>
                                <RevAlert tone="info" title={t('event.new.credits.title')}>
                                    <span className="rev-help">{t('event.new.credits.body')}</span>
                                </RevAlert>
                            </div>
                            <div className="rev-cols__aside">
                                <RevCard title={t('event.new.steps.title')}>
                                    <RevTipRow n="01" title={t('event.new.steps.s1.title')} detail={t('event.new.steps.s1.detail')} />
                                    <RevTipRow n="02" title={t('event.new.steps.s2.title')} detail={t('event.new.steps.s2.detail')} />
                                    <RevTipRow n="03" title={t('event.new.steps.s3.title')} detail={t('event.new.steps.s3.detail')} />
                                </RevCard>
                            </div>
                        </div>
                    </>
                )}
                {isEdit ? (
                    <RevBarra sangre error={errors.root?.message} ok={saved ? t('event.basicData.saved') : undefined}>
                        {cancelButton}
                        <RevButton data-testid="saveOrCreate" type="submit" variant="primary"
                            loading={loading || isSubmitting}
                            disabled={(!isDirty && !enableSubmit) || loading || isSubmitting}>
                            {t('generic.saveButton.text')}
                        </RevButton>
                    </RevBarra>
                ) : (
                    <RevBarra sangre error={errors.root?.message || (!watch('name')?.trim() ? t('event.new.error.name') : null)}>
                        {cancelButton}
                        <RevButton data-testid="saveOrCreate" type="submit" variant="primary"
                            loading={loading || isSubmitting}
                            disabled={(!isDirty && !enableSubmit) || loading || isSubmitting || !watch('name')?.trim()}>
                            {t('event.basicData.createButton.text')}
                        </RevButton>
                    </RevBarra>
                )}
            </form>
            {showDeleteModal &&
                <Modal
                    showCancelButton
                    showConfirmButton
                    title={t('event.deleteEventAlert.title')}
                    message={t('event.deleteEventAlert.message')}
                    cancelButtonText={t('generic.alertButton.cancel')}
                    confirmButtonText={t('generic.alertButton.delete')}
                    onCancel={() => setShowDeleteModal(false)}
                    onConfirm={() => { setShowDeleteModal(false); dispatchDelete(); }}
                    isDanger
                    loading={loading}
                />
            }
        </>
    );
}

export default EventForm;
